// src/auth/AuthContext.jsx
// FASE 1.B — Contexto único de auth + rol + acceso.
// userData en vivo via onSnapshot para que cualquier cambio en /users/{uid}
// (rol, accessUntil, telegramChatId) re-renderice consumidores sin reload.
//
// FIX #1 (race conditions auditoría): cuando user cambia de identidad
// (login fresh, switch account), forzamos loading=true y userData=null
// para que ningún consumer pueda navegar con userData stale del usuario
// previo o null mientras espera el primer snap de Firestore. Sin esto,
// admins y clientes con pago activo eran enviados a /paywall justo
// después del login porque destinationFor(null) → /dashboard y luego
// ProtectedRoute requireAccess + hasAccess=false (computeHasAccess(null))
// → /paywall ANTES de que llegue el snap.
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut as fbSignOut, getAuth } from 'firebase/auth';
import { doc, onSnapshot, getFirestore, updateDoc, serverTimestamp } from 'firebase/firestore';
import app from '../firebase';

const auth = getAuth(app);
const db = getFirestore(app);

const AuthContext = createContext(null);

function computeHasAccess(userData) {
  if (!userData) return false;
  if (userData.role === 'admin') return true;
  // Beta vigente: mismo patrón que computeHasActiveAccess. Necesario para
  // que VerifyEmail/SinPermisos/useSmartBack/MiCuentaModal/classifyScreen
  // de TelegramConnect traten al beta tester como "con acceso" para
  // navegación y UX. Sin esto, beta testers eran redirigidos a /paywall
  // por flujos que consumen `hasAccess` aunque ProtectedRoute (que usa
  // hasActiveAccess) los dejaba pasar.
  const beta = userData.betaAccess;
  if (beta && beta.active === true && beta.expiresAt) {
    const betaTs = beta.expiresAt.seconds
      ? beta.expiresAt.seconds * 1000
      : new Date(beta.expiresAt).getTime();
    if (betaTs > Date.now()) return true;
  }
  if (userData.accessUntil) {
    const ts = userData.accessUntil.seconds
      ? userData.accessUntil.seconds * 1000
      : new Date(userData.accessUntil).getTime();
    return ts > Date.now();
  }
  // Compat temporal con clientes legacy (Flow Cargo Automático, FASE 0).
  // TODO FASE C: eliminar cuando todos migren a accessUntil.
  if (userData.pagoActivo === true) return true;
  return false;
}

// FIX paywall bypass: variante estricta sin fallback `pagoActivo` legacy.
// Uso exclusivo en ProtectedRoute para guard de rutas con requireAccess.
// El modelo de negocio dice: admin → bypass paywall · cliente → necesita
// accessUntil > now. Cualquier otro estado (pagoActivo legacy true, doc
// con campos viejos, etc.) NO debe dar acceso al dashboard.
function computeHasActiveAccess(userData) {
  if (!userData) return false;
  if (userData.role === 'admin') return true;
  // Beta vigente: acceso por betaAccess aunque no haya accessUntil pagado.
  // Validación por fecha: si expiresAt <= now, NO da acceso (auto-cierre
  // sin tocar el campo). Permite tracking analítico (active=true expirado).
  const beta = userData.betaAccess;
  if (beta && beta.active === true && beta.expiresAt) {
    const betaTs = beta.expiresAt.seconds
      ? beta.expiresAt.seconds * 1000
      : new Date(beta.expiresAt).getTime();
    if (betaTs > Date.now()) return true;
  }
  if (!userData.accessUntil) return false;
  const ts = userData.accessUntil.seconds
    ? userData.accessUntil.seconds * 1000
    : new Date(userData.accessUntil).getTime();
  return ts > Date.now();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDocError, setUserDocError] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const retriesRef = useRef(0);
  const lastUidRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      const newUid = fbUser?.uid || null;
      const uidChanged = newUid !== lastUidRef.current;
      lastUidRef.current = newUid;

      setUser(fbUser);

      if (!fbUser) {
        setUserData(null);
        setLoading(false);
      } else if (uidChanged) {
        // Identidad cambió (login fresh o switch account) — invalidar
        // userData stale y forzar loading=true hasta que llegue el primer
        // snap del nuevo uid. Sin esto, consumers reciben (user=newUid,
        // userData=null|stale, loading=false) y navegan mal.
        setUserData(null);
        setLoading(true);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let retryTimer = null;
    // FASE 3.5 — Backfill de emailVerified en Firestore.
    // Si un usuario verificó su cuenta desde un device sin sesión (el
    // sync principal en AuthAction.jsx requiere auth.currentUser), al
    // primer login de vuelta detectamos que Auth dice verified=true pero
    // Firestore no — corregimos una sola vez. Esto dispara el trigger
    // onUserVerifiedSendWelcome retroactivamente.
    let backfilled = false;
    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      async (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setUserData({ uid: user.uid, ...data });
          setUserDocError(false);
          retriesRef.current = 0;
          if (
            !backfilled &&
            user.emailVerified === true &&
            data.emailVerified !== true
          ) {
            backfilled = true;
            try {
              await updateDoc(doc(db, 'users', user.uid), {
                emailVerified: true,
                verifiedAt: serverTimestamp(),
              });
            } catch (e) {
              console.warn('[AuthContext] Backfill emailVerified failed:', e);
            }
          }
        } else {
          setUserData({ uid: user.uid, role: 'client', accessUntil: null });
        }
        setLoading(false);
      },
      (err) => {
        // NO bajar loading sin datos: con loading=false y userData=null,
        // hasActiveAccess da false y ProtectedRoute manda al paywall a un
        // cliente con acceso vigente (paywall intermitente que se arregla
        // recargando). Se reintenta con backoff y solo se rinde tras varios
        // intentos, dejando el estado marcado como error (no como "sin
        // acceso"), que ProtectedRoute distingue.
        console.error('AuthContext userDoc error:', err);
        setUserDocError(true);
        if (retriesRef.current < 4) {
          const delay = 800 * (retriesRef.current + 1);
          retriesRef.current += 1;
          retryTimer = setTimeout(() => setRetryTick((t) => t + 1), delay);
        } else {
          setLoading(false);
        }
      }
    );
    return () => {
      unsub();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [user, retryTick]);

  const signOut = () => fbSignOut(auth);
  const hasAccess = computeHasAccess(userData);
  const hasActiveAccess = computeHasActiveAccess(userData);

  const value = {
    user,
    userData,
    loading,
    role: userData?.role || null,
    isAdmin: userData?.role === 'admin',
    isClient: userData?.role === 'client',
    hasAccess,
    hasActiveAccess,
    // El guard necesita distinguir "sin acceso" de "todavía no sé":
    // userDataReady=false ⇒ no hay datos para decidir el paywall.
    userDataReady: !!userData,
    userDocError,
    accessUntil: userData?.accessUntil || null,
    emailVerified: user?.emailVerified || false,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}
